/**
 * @license
 * Copyright 2023 Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
import { deepQuerySelectorAll, getActiveElement } from '@tylertech/forge-core';
import { BaseAdapter } from '../../core/base/base-adapter';
import { LIST_ITEM_CONSTANTS } from '../list-item';
import { LIST_CONSTANTS } from './list-constants';
/**
 * The DOM adapter for the `<forge-list>` component.
 */
export class ListAdapter extends BaseAdapter {
    constructor(component) {
        super(component);
    }
    initializeAccessibility() {
        if (!this._component.hasAttribute('role')) {
            this._component.setAttribute('role', 'list');
        }
    }
    /**
     * Adds an event listener to the `<forge-list>` host element.
     * @param {string} type The event type.
     * @param {Function} listener The event callback.
     */
    addListener(type, listener) {
        this._component.addEventListener(type, listener);
    }
    /**
     * Removes an event listener to the `<forge-list>` host element.
     * @param {string} type The event type.
     * @param {Function} listener The event callback.
     */
    removeListener(type, listener) {
        this._component.removeEventListener(type, listener);
    }
    /**
     * Returns all child `<forge-list-item>` elements.
     */
    getListItems() {
        return Array.from(this._component.children).filter(child => child.tagName === LIST_ITEM_CONSTANTS.elementName.toUpperCase());
    }
    /**
     * Sets focus to the next item in the list.
     */
    focusNextListItem() {
        const listItems = deepQuerySelectorAll(this._component, LIST_CONSTANTS.selectors.FOCUSABLE_LIST_ITEMS, false);
        if (listItems && listItems.length > 0) {
            const focusedListItemIndex = listItems.indexOf(getActiveElement());
            const nextIndex = focusedListItemIndex < listItems.length - 1 ? focusedListItemIndex + 1 : 0;
            if (nextIndex <= listItems.length - 1) {
                listItems[nextIndex].focus();
            }
        }
    }
    /**
     * Sets focus to the previous item in the list.
     */
    focusPreviousListItem() {
        const listItems = deepQuerySelectorAll(this._component, LIST_CONSTANTS.selectors.FOCUSABLE_LIST_ITEMS, false);
        if (listItems && listItems.length > 0) {
            const focusedListItemIndex = listItems.indexOf(getActiveElement());
            const nextIndex = focusedListItemIndex > 0 ? focusedListItemIndex - 1 : listItems.length - 1;
            if (nextIndex >= 0) {
                listItems[nextIndex].focus();
            }
        }
    }
    /**
     * Sets focus to the first item in the list.
     */
    focusFirstListItem() {
        const listItems = deepQuerySelectorAll(this._component, LIST_CONSTANTS.selectors.FOCUSABLE_LIST_ITEMS, false);
        if (listItems && listItems.length > 0) {
            listItems[0].focus();
        }
    }
    /**
     * Sets focus to the last item in the list.
     */
    focusLastListItem() {
        const listItems = deepQuerySelectorAll(this._component, LIST_CONSTANTS.selectors.FOCUSABLE_LIST_ITEMS, false);
        if (listItems && listItems.length > 0) {
            listItems[listItems.length - 1].focus();
        }
    }
    setSelectedListItems(values) {
        const listItems = Array.from(this._component.querySelectorAll(LIST_ITEM_CONSTANTS.elementName));
        if (listItems && listItems.length) {
            for (const item of listItems) {
                item.selected = values.includes(item.value);
            }
        }
    }
    updateListItems(cb) {
        this.getListItems().forEach(li => cb(li));
    }
}
